import { useState, useRef, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Navigate, Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { usePageMeta } from "@/hooks/usePageMeta";

type AuthMode = "signin" | "signup" | "forgot";

// â"€â"€ Sidebar-identical logo â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€

function TerminalLogo() {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <svg width="28" height="20" viewBox="0 0 28 20" fill="none" aria-hidden="true">
          <polyline
            points="2,17 7,3 13,12 19,3 25,10"
            stroke="#1d9e75" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"
          />
          <circle cx="2" cy="17" r="2" fill="#1d9e75" />
          <circle cx="25" cy="10" r="2" fill="#1d9e75" />
        </svg>
        <div>
          <div style={{ fontFamily: "monospace", fontSize: 13, fontWeight: 700, letterSpacing: "0.18em", color: "rgba(255,255,255,0.7)", textTransform: "uppercase", lineHeight: 1.3 }}>MARGIN</div>
          <div style={{ fontFamily: "monospace", fontSize: 13, fontWeight: 700, letterSpacing: "0.18em", color: "rgba(255,255,255,0.7)", textTransform: "uppercase", lineHeight: 1.3 }}>METRICS</div>
        </div>
      </div>
      <div style={{ fontFamily: "monospace", fontSize: 9, letterSpacing: "0.15em", color: "rgba(255,255,255,0.2)", textTransform: "uppercase" }}>
        Trading Performance Platform
      </div>
    </div>
  );
}

// â"€â"€ Input component â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€

function TerminalInput({
  type,
  value,
  onChange,
  placeholder,
  required,
  minLength,
  label,
  focused,
  onFocus,
  onBlur,
  right,
}: {
  type: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  required?: boolean;
  minLength?: number;
  label: string;
  focused: boolean;
  onFocus: () => void;
  onBlur: () => void;
  right?: React.ReactNode;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <label style={{ fontFamily: "monospace", fontSize: 9, letterSpacing: "0.1em", textTransform: "uppercase", color: "rgba(255,255,255,0.35)" }}>
          {label}
        </label>
        {right}
      </div>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        minLength={minLength}
        onFocus={onFocus}
        onBlur={onBlur}
        style={{
          width: "100%",
          padding: "10px 12px",
          background: "rgba(255,255,255,0.04)",
          border: focused ? "1px solid rgba(255,255,255,0.2)" : "1px solid rgba(255,255,255,0.06)",
          color: "rgba(255,255,255,0.9)",
          fontFamily: "monospace",
          fontSize: 16,
          outline: "none",
          transition: "border-color 150ms",
          boxSizing: "border-box",
        }}
      />
    </div>
  );
}

// â"€â"€ Main component â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€

export default function AuthPage() {
  const { user, loading, signIn, signUp, resetPassword } = useAuth();

  usePageMeta({
    title: "Sign In",
    description: "Sign in to Margin Metrics -- your premium trading analytics and journal platform.",
  });

  // Honor ?mode= from the landing CTAs — "Start Free" links to ?mode=signup and
  // must open the sign-up form, not the login form (a new visitor on a login
  // screen bounces). Anything else (incl. ?mode=login) opens sign-in.
  const [mode, setMode] = useState<AuthMode>(() =>
    new URLSearchParams(window.location.search).get("mode") === "signup" ? "signup" : "signin"
  );
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [transitioning, setTransitioning] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const formRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const err = params.get("error");
    if (err) {
      toast.error(`Sign-in failed: ${err}`);
      const url = new URL(window.location.href);
      url.searchParams.delete("error");
      window.history.replaceState({}, "", url.toString());
    }
  }, []);

  const switchMode = (newMode: AuthMode) => {
    setTransitioning(true);
    setTimeout(() => {
      setMode(newMode);
      setPassword("");
      setTimeout(() => setTransitioning(false), 20);
    }, 200);
  };

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", background: "#000", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ fontFamily: "monospace", fontSize: 10, letterSpacing: "0.15em", color: "rgba(255,255,255,0.3)", textTransform: "uppercase" }}>
          Initialising...
        </div>
      </div>
    );
  }

  if (user) return <Navigate to="/" replace />;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (mode === "forgot") {
        const { error } = await resetPassword(email);
        if (error) {
          toast.error(error.message);
        } else {
          toast.success("Password reset link sent! Check your email.");
          switchMode("signin");
        }
      } else if (mode === "signup") {
        const { error } = await signUp(email, password);
        if (error) {
          toast.error(error.message);
        } else {
          toast.success("Check your email to confirm your account!");
        }
      } else {
        const { error } = await signIn(email, password);
        if (error) {
          toast.error(error.message);
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  const validatePassword = (pw: string) => {
    if (pw.length < 8) return "At least 8 characters";
    if (!/\d/.test(pw)) return "Must include a number";
    if (!/[A-Za-z]/.test(pw)) return "Must include a letter";
    return "";
  };

  const headings: Record<AuthMode, { title: string; subtitle: string }> = {
    signin: { title: "ACCESS TERMINAL", subtitle: "Sign in to your trading dashboard" },
    signup: { title: "CREATE ACCOUNT", subtitle: "Start tracking your trading performance" },
    forgot: { title: "RESET PASSWORD", subtitle: "We'll send a reset link to your email" },
  };

  const socialButtonStyle: React.CSSProperties = {
    width: "100%",
    padding: "13px 16px",
    minHeight: 48,
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.08)",
    color: "rgba(255,255,255,0.8)",
    fontFamily: "monospace",
    fontSize: 11,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    cursor: "pointer",
    transition: "border-color 150ms, background 150ms",
    boxSizing: "border-box" as const,
  };

  return (
    <div style={{ minHeight: "100dvh", background: "#000", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ width: "100%", maxWidth: 480 }}>

        {/* Logo */}
        <div style={{ marginBottom: 44 }}>
          <TerminalLogo />
        </div>

        {/* Card */}
        <div className="border border-white/[0.08] p-6 sm:p-10" style={{  }}>

          {/* Transition wrapper */}
          <div
            ref={formRef}
            style={{
              opacity: transitioning ? 0 : 1,
              transform: transitioning ? "translateY(8px)" : "translateY(0)",
              transition: "opacity 200ms ease-out, transform 200ms ease-out",
            }}
          >

            {/* Heading */}
            <div style={{ marginBottom: 28, textAlign: "center" }}>
              {mode === "forgot" && (
                <button
                  type="button"
                  onClick={() => switchMode("signin")}
                  style={{ fontFamily: "monospace", fontSize: 9, color: "rgba(255,255,255,0.3)", letterSpacing: "0.1em", background: "none", border: "none", cursor: "pointer", marginBottom: 14, display: "block", textTransform: "uppercase" }}
                >
                  &larr; Back to sign in
                </button>
              )}
              <h2 style={{ fontFamily: "monospace", fontSize: 16, fontWeight: 700, letterSpacing: "0.12em", color: "rgba(255,255,255,0.92)", margin: "0 0 8px 0" }}>
                {headings[mode].title}
              </h2>
              <p style={{ fontFamily: "monospace", fontSize: 11, color: "rgba(255,255,255,0.35)", letterSpacing: "0.05em", margin: 0 }}>
                {headings[mode].subtitle}
              </p>
            </div>

            {/* Social sign-in buttons */}
            {mode !== "forgot" && (
              <>
                <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 20 }}>
                  <button
                    type="button"
                    style={socialButtonStyle}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.12)"; e.currentTarget.style.background = "rgba(255,255,255,0.06)"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.06)"; e.currentTarget.style.background = "rgba(255,255,255,0.04)"; }}
                    onClick={async () => {
                      const { error } = await supabase.auth.signInWithOAuth({
                        provider: "google",
                        options: {
                          redirectTo: "https://margin-metrics.com/auth/callback",
                          queryParams: { access_type: "offline", prompt: "consent" },
                        },
                      });
                      if (error) toast.error(error.message);
                    }}
                  >
                    <svg viewBox="0 0 24 24" style={{ width: 16, height: 16, flexShrink: 0 }} aria-hidden="true">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                    </svg>
                    Continue with Google
                  </button>
                  {/* Apple sign-in removed for launch — provider not configured yet (post-launch). */}
                </div>

                {/* Divider */}
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
                  <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,0.06)" }} />
                  <span style={{ fontFamily: "monospace", fontSize: 9, color: "rgba(255,255,255,0.2)", letterSpacing: "0.1em", textTransform: "uppercase" }}>or email</span>
                  <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,0.06)" }} />
                </div>
              </>
            )}

            {/* Email form */}
            <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <TerminalInput
                type="email"
                label="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="trader@example.com"
                required
                focused={focusedField === "email"}
                onFocus={() => setFocusedField("email")}
                onBlur={() => setFocusedField(null)}
              />

              {mode !== "forgot" && (
                <TerminalInput
                  type="password"
                  label="Password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (mode === "signup") setPasswordError(validatePassword(e.target.value));
                  }}
                  placeholder="Min. 8 characters"
                  required
                  minLength={8}
                  focused={focusedField === "password"}
                  onFocus={() => setFocusedField("password")}
                  onBlur={() => setFocusedField(null)}
                  right={
                    mode === "signin" ? (
                      <button
                        type="button"
                        onClick={() => switchMode("forgot")}
                        style={{ fontFamily: "monospace", fontSize: 9, color: "rgba(255,255,255,0.25)", background: "none", border: "none", cursor: "pointer", letterSpacing: "0.08em", textTransform: "uppercase" }}
                      >
                        Forgot?
                      </button>
                    ) : undefined
                  }
                />
              )}

              {mode === "signup" && passwordError && password.length > 0 && (
                <p style={{ fontFamily: "monospace", fontSize: 9, color: "#ef4444", margin: "-4px 0 0 0" }}>{passwordError}</p>
              )}

              <button
                type="submit"
                disabled={submitting || (mode === "signup" && !!passwordError && password.length > 0)}
                style={{
                  width: "100%",
                  padding: "14px 0",
                  background: submitting ? "rgba(29,158,117,0.7)" : "#1d9e75",
                  color: "#000",
                  fontFamily: "monospace",
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  border: "none",
                  cursor: submitting ? "not-allowed" : "pointer",
                  marginTop: 4,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  transition: "opacity 150ms",
                  opacity: (mode === "signup" && !!passwordError && password.length > 0) ? 0.5 : 1,
                }}
              >
                {submitting ? (
                  <Loader2 style={{ width: 14, height: 14, animation: "spin 1s linear infinite" }} />
                ) : mode === "forgot" ? (
                  "Send Reset Link"
                ) : mode === "signup" ? (
                  "Create Account"
                ) : (
                  "Sign In"
                )}
              </button>
            </form>
          </div>
        </div>

        {/* Mode toggle */}
        {mode !== "forgot" && (
          <p style={{ fontFamily: "monospace", fontSize: 10, color: "rgba(255,255,255,0.25)", textAlign: "center", marginTop: 16, letterSpacing: "0.04em" }}>
            {mode === "signup" ? "Already have an account?" : "Don't have an account?"}{" "}
            <button
              onClick={() => switchMode(mode === "signup" ? "signin" : "signup")}
              style={{ color: "#1d9e75", background: "none", border: "none", cursor: "pointer", fontFamily: "monospace", fontSize: 10, letterSpacing: "0.04em" }}
            >
              {mode === "signup" ? "Sign in" : "Sign up"}
            </button>
          </p>
        )}

        {/* Legal links */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, marginTop: 14 }}>
          <Link to="/terms" style={{ fontFamily: "monospace", fontSize: 9, color: "rgba(255,255,255,0.2)", textDecoration: "none", letterSpacing: "0.08em", textTransform: "uppercase" }}>Terms</Link>
          <span style={{ color: "rgba(255,255,255,0.1)", fontSize: 9 }}>·</span>
          <Link to="/privacy" style={{ fontFamily: "monospace", fontSize: 9, color: "rgba(255,255,255,0.2)", textDecoration: "none", letterSpacing: "0.08em", textTransform: "uppercase" }}>Privacy</Link>
        </div>

      </div>
    </div>
  );
}
